import type { HTMLAttributes, ReactNode } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import additionalRequestSrc from './icons/empty-state-additional-request.svg';
import connectivitySrc from './icons/empty-state-connectivity.svg';
import connectorSrc from './icons/empty-state-connector.svg';
import dashboardSrc from './icons/empty-state-dashboard.svg';
import documentSrc from './icons/empty-state-document.svg';
import genericSrc from './icons/empty-state-generic.svg';
import incidentSrc from './icons/empty-state-incident.svg';
import orderSrc from './icons/empty-state-order.svg';
import planningSrc from './icons/empty-state-planning.svg';
import referenceSrc from './icons/empty-state-reference.svg';
import stockSrc from './icons/empty-state-stock.svg';
import transportSrc from './icons/empty-state-transport.svg';

const ICON_SRC = {
    'additional-request': additionalRequestSrc,
    connectivity: connectivitySrc,
    connector: connectorSrc,
    dashboard: dashboardSrc,
    document: documentSrc,
    automation: genericSrc,
    generic: genericSrc,
    incident: incidentSrc,
    order: orderSrc,
    planning: planningSrc,
    reference: referenceSrc,
    stock: stockSrc,
    transport: transportSrc,
} as const;

export type EmptyStateIconName = keyof typeof ICON_SRC;

export interface EmptyStateProps {
    icon?: EmptyStateIconName | IconDefinition;
    picture?: {
        src: string;
        width: number;
        height: number;
    };
    paragraph: string;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    title?: string;
    children?: ReactNode;
}

export const EmptyState = ({
    icon,
    picture,
    paragraph,
    dataTestid,
    htmlAttributes,
    title,
    children,
}: EmptyStateProps) => (
    <div
        className='flex flex-col items-center gap-y-2 pb-8'
        data-testid={dataTestid}
        data-component-name='EmptyState'
        {...htmlAttributes}
    >
        <div className='shrink'>
            {typeof icon === 'string' ? (
                <img
                    src={ICON_SRC[icon]}
                    alt=''
                />
            ) : icon ? (
                <FontAwesomeIcon
                    icon={icon}
                    className='text-3xl text-gray-500'
                />
            ) : null}
            {picture && (
                <img
                    src={picture.src}
                    width={picture.width}
                    height={picture.height}
                    alt=''
                />
            )}
        </div>
        {title && <p className='text-base font-semibold text-gray-900'>{title}</p>}
        <p className='max-w-md whitespace-pre-line text-center text-sm text-gray-500'>{paragraph}</p>
        {children && <div className='mt-2 flex gap-x-3'>{children}</div>}
    </div>
);
